import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/i18n/I18nContext";
import { IMAGES, ROOMS, ROOM_SPECS } from "@/data/site";
import { Reveal } from "@/components/Reveal";
import { Lightbox } from "@/components/Lightbox";
import { Users, ArrowRight, Ruler, BedDouble, Check, Expand, Sun, KeyRound } from "lucide-react";

const RoomBlock = ({ slug, reverse }) => {
  const { t, lang } = useI18n();
  const key = slug.split("_")[0];
  const accent = ROOMS.find(r => r.slug === slug).color;
  const images = IMAGES.rooms[slug];
  const spec = ROOM_SPECS[slug];
  const [lbIndex, setLbIndex] = useState(null);
  const thumbs = images.slice(1, 4);
  const extra = images.length - 1 - thumbs.length; // hidden images beyond shown thumbs
  return (
    <section id={slug} data-testid={`room-section-${slug}`} className="py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className={`grid lg:grid-cols-2 gap-12 md:gap-16 items-center ${reverse ? "lg:[&>*:first-child]:order-2" : ""}`}>
          <Reveal>
            <div className="flex flex-col gap-3 md:gap-4 h-[420px] md:h-[540px]">
              <button type="button" onClick={() => setLbIndex(0)} data-testid={`room-photo-${slug}-0`}
                className="flex-1 overflow-hidden rounded-2xl relative group cursor-pointer min-h-0">
                <img src={images[0]} alt={t(`rooms.${key}.name`)} className="w-full h-full object-cover img-zoom" />
                {slug === "blu_marino" && (
                  <span data-testid={`badge-exclusive-terrace-${slug}`}
                    className="absolute top-3 left-3 z-10 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/90 backdrop-blur text-[#1D3557] text-xs font-medium shadow-sm">
                    <Sun className="w-3.5 h-3.5" /> {t("rooms.exclusiveTerraceBadge")}
                  </span>
                )}
                {slug === "rosso_granato" && (
                  <span data-testid={`badge-family-${slug}`}
                    className="absolute top-3 left-3 z-10 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/90 backdrop-blur text-[#1D3557] text-xs font-medium shadow-sm">
                    <Users className="w-3.5 h-3.5" /> {t("rooms.familyFriendlyBadge")}
                  </span>
                )}
                <span className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/40 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <Expand className="w-4 h-4" />
                </span>
              </button>
              {images.length > 1 && (
                <div className="grid grid-cols-3 gap-3 md:gap-4 h-24 md:h-36 flex-shrink-0">
                  {thumbs.map((src, i) => {
                    const isLast = i === thumbs.length - 1 && extra > 0;
                    return (
                      <button type="button" key={i} onClick={() => setLbIndex(i + 1)}
                        data-testid={`room-photo-${slug}-${i + 1}`}
                        className="overflow-hidden rounded-2xl relative group cursor-pointer">
                        <img src={src} alt="" className="w-full h-full object-cover img-zoom" />
                        {isLast && (
                          <span className="absolute inset-0 bg-black/55 text-white flex items-center justify-center text-lg font-medium">
                            +{extra + 1}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </Reveal>
          <Reveal delay={150}>
            <div>
              <div className="flex items-center gap-2 mb-4">
                <span className="w-3 h-3 rounded-full" style={{ background: accent }} />
                <span className="text-xs uppercase tracking-[0.3em] text-[#5C5C5C]">{t(`rooms.${key}.tag`)}</span>
              </div>
              <h2 className="font-display text-4xl md:text-5xl text-[#1A1A1A] tracking-tight" style={{ color: accent }}>
                {t(`rooms.${key}.name`)}
              </h2>
              <p className="mt-5 text-[#5C5C5C] text-base md:text-lg leading-relaxed">{t(`rooms.${key}.desc`)}</p>

              <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3">
                <div className="flex items-center gap-2 text-sm text-[#1A1A1A]">
                  <Ruler className="w-4 h-4 text-[#345B73]" /> <span><span className="text-[#5C5C5C]">{t("rooms.sizeLabel")}:</span> {spec.size}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-[#1A1A1A]">
                  <BedDouble className="w-4 h-4 text-[#345B73]" /> {spec.beds[lang] || spec.beds.it}
                </div>
                <div className="flex items-center gap-2 text-sm text-[#1A1A1A]">
                  <Users className="w-4 h-4 text-[#345B73]" /> {spec.maxGuests} {t("common.maxGuests")}
                </div>
              </div>

              <div className="mt-8">
                <div className="text-xs uppercase tracking-[0.3em] text-[#5C5C5C] mb-4">{t("rooms.amenitiesTitle")}</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2.5">
                  {spec.amenities.map((a) => (
                    <div key={a} className="flex items-center gap-2 text-sm text-[#1A1A1A]">
                      <Check className="w-4 h-4 flex-shrink-0" style={{ color: accent }} /> {t(`rooms.amenities.${a}`)}
                    </div>
                  ))}
                </div>
              </div>

              <Link to={`/${lang}/prenota?room=${slug}`} data-testid={`book-room-${slug}`}
                className="mt-10 inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-white font-medium transition-all"
                style={{ background: accent }}>
                {t("nav.bookNow")} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </div>
      <Lightbox images={images} index={lbIndex} onIndexChange={setLbIndex}
        onClose={() => setLbIndex(null)} alt={t(`rooms.${key}.name`)} />
    </section>
  );
};

export default function RoomsPage() {
  const { t } = useI18n();
  return (
    <>
      <section className="pt-36 pb-12 bg-[#F2EFEB]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="text-xs uppercase tracking-[0.3em] text-[#345B73] mb-4">I Lake It Bellagio</div>
            <h1 className="font-display text-5xl md:text-6xl text-[#1A1A1A] tracking-tight max-w-3xl">{t("rooms.title")}</h1>
            <p className="mt-5 text-lg text-[#5C5C5C] max-w-2xl">{t("rooms.subtitle")}</p>
            <div data-testid="rooms-self-checkin" className="mt-7 flex items-start gap-3 max-w-3xl px-5 py-4 rounded-2xl bg-white border border-[#1D3557]/15">
              <KeyRound className="w-5 h-5 text-[#1D3557] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-[#1D3557]">{t("selfCheckin.title")}</p>
                <p className="mt-1 text-sm text-[#5C5C5C] leading-relaxed">{t("selfCheckin.text")}</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
      {ROOMS.map((r, i) => <RoomBlock key={r.slug} slug={r.slug} reverse={i % 2 === 1} />)}
    </>
  );
}
